import { Directive, inject, OnInit, Signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';
import { catchError, finalize, Observable, tap, throwError } from 'rxjs';

import { FormUtils, Mapper, MarkFormAsTouchedIfInvalid } from 'tiime-expert-utils';
import { DialogBase } from 'tiime-material';

import { CustomAccountingPeriodForm, CustomAccountingPeriodLabel } from '@forms/custom-accounting-period-form';
import { DateHelper } from '@helpers/date.helper';
import { AppStoreState } from '@interfaces/app-store-state';
import { AccountingPeriod } from '@models/accounting-period';

@Directive()
export abstract class CustomAccountingPeriodDialogBase<T>
  extends DialogBase<CustomAccountingPeriodDialogBase<T>>
  implements OnInit
{
  form: CustomAccountingPeriodForm;

  readonly store: Store<AppStoreState> = inject<Store<AppStoreState>>(Store<AppStoreState>);

  protected abstract needToRemoveOneDayToMaxDate: boolean;
  protected abstract accountingPeriods: Signal<AccountingPeriod[]>;
  protected abstract customAccountingPeriodLabel: CustomAccountingPeriodLabel;
  protected abstract title: string;
  protected abstract startDatePlaceholder: string;
  protected abstract endDatePlaceholder: string;
  protected abstract instruction: string | null;

  readonly mapToMinDate: Mapper<string, Date> = (startDate: string) =>
    DateHelper.toDate(DateHelper.add(startDate, 1, 'day'));

  readonly mapToMaxDate: Mapper<string, Date> = (endDate: string) =>
    this.needToRemoveOneDayToMaxDate
      ? DateHelper.toDate(DateHelper.add(endDate, -1, 'day'))
      : DateHelper.toDate(endDate);

  constructor(protected dialogRef: MatDialogRef<CustomAccountingPeriodDialogBase<T>>) {
    super(dialogRef);
  }

  @MarkFormAsTouchedIfInvalid('form')
  validate(): void {
    this.isLoading.set(true);
    this.form.disable(FormUtils.shouldNotEmitEvent);

    this.save()
      .pipe(
        tap((result: T) => this.close(result)),
        catchError(err => {
          this.form.enable(FormUtils.shouldNotEmitEvent);
          return throwError(err);
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe();
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.form = new CustomAccountingPeriodForm(this.customAccountingPeriodLabel);
    this.initFormValue();
  }

  compareAccountingPeriodFn(value: AccountingPeriod | null, option: AccountingPeriod): boolean {
    return value?.id === option.id;
  }

  abstract save(): Observable<T>;

  abstract initFormValue(): void;
}
